/**
 * The rail a position's flow runs on: its steps, and where a position stands
 * on it. Pure and shared by the in-progress bar (through the server's
 * progress feed) and the Buy/Sell dialog, so the chip and the window move
 * alike (John, Nov 1 2026: the chip looked slower than the window). Every
 * sale shows the same steps whichever wallet ends up selling (a bank wallet
 * gathering its tokens, or a fresh one being funded and collecting them both
 * count as Funding), so the rail a sale starts on is the rail it finishes
 * on. A sell's last leg: on exchange custody the proceeds landing at the
 * exchange, else the SOL coming home. A sale clearing the insider mark first
 * (Remove Insider Mark, John, Nov 2 2026) has one more step before Selling,
 * and keeps it from start to finish.
 */
export type Flow = 'buy' | 'vaultBuy' | 'sell' | 'vaultSell'

/** what placing a position on its rail needs; the list's rows and the dialog's record both carry it */
export type RailPosition = {
  kind: string
  status: string
  spot: 'buy' | 'sell' | null
  exchange: string | null
  custodies: boolean
  custodyDepositId: string | null
  houseSale: boolean
  houseStep: 'wallet' | 'clearing' | null
}

export function stepsOf(flow: Flow, exchange: boolean, house = false): readonly string[] {
  switch (flow) {
    case 'buy':
      return ['Funding', 'Buying', 'Open']
    case 'vaultBuy':
      return ['Funding', 'Buying', 'Depositing', 'Done']
    case 'sell':
    case 'vaultSell':
      return ['Funding', ...(house ? ['Clearing'] : []), 'Selling', exchange ? 'Depositing' : 'Returning', 'Done']
  }
}

const ORDER: Record<Flow, Record<string, number>> = {
  buy: { funding: 0, funded: 1, buying: 1, open: 2 },
  vaultBuy: { funding: 0, funded: 1, buying: 1, custodying: 2, returning: 3, closed: 3 },
  sell: { open: 0, selling: 1, returning: 2, closed: 3 },
  vaultSell: { funding: 0, collecting: 0, selling: 1, returning: 2, closed: 3 },
}

export function flowOf(p: RailPosition, mode: 'open' | 'sell'): Flow {
  if (p.kind === 'vaultSell') return 'vaultSell'
  if (mode === 'open') return p.custodies || p.custodyDepositId !== null || p.status === 'custodying' ? 'vaultBuy' : 'buy'
  return 'sell'
}

export function stepOf(flow: Flow, p: RailPosition): number {
  // a spot trade staged in an open bank wallet: gathering its tokens, or waiting for its SOL; a sale clearing the insider
  // mark moves to its Clearing step once the wallet buys from the pool
  if (p.spot !== null && p.status === 'open') return p.houseSale && p.houseStep === 'clearing' ? 1 : 0
  const i = ORDER[flow][p.status] ?? -1
  // the Clearing step sits before Selling
  return p.houseSale && i > 0 ? i + 1 : i
}

/**
 * Where the bar's chip sits for a position: the same step of the same rail
 * as its dialog. `settling`: the sale is done and its proceeds are still
 * landing at the exchange, the step before Done.
 */
export function bandOf(p: RailPosition, mode: 'open' | 'sell', settling: boolean): { floor: number; ceiling: number } {
  const flow = flowOf(p, mode)
  const count = stepsOf(flow, p.exchange !== null, p.houseSale).length
  const i = Math.max(0, Math.min(count - 1, settling ? count - 2 : stepOf(flow, p)))
  return { floor: i / count + 0.02, ceiling: (i + 1) / count - 0.02 }
}
